import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Input, Badge } from "@realtyflows/ui";
import { listMyOrgs, getSsoConfig, updateSsoConfig } from "../api";

/**
 * SsoSettings — V5 Feature 5.9 UI
 *
 * Organization owner configures SAML or OIDC SSO.
 *   - SAML: paste entity ID, SSO URL, and certificate from IdP metadata
 *   - OIDC: paste issuer, client id, client secret, redirect URI
 *
 * Plus the optional behavior knobs:
 *   - Auto-route by email domain (e.g. @samplerealty.com → this org's SSO)
 *   - JIT user provisioning (auto-create users on first SSO login)
 *   - Default role for newly-provisioned users
 *
 * Notes for implementers: the SAML/OIDC handshake routes are stubbed
 * server-side (passport-saml / openid-client need to be installed before
 * actual SSO flows work). The config persistence is fully functional.
 */
export default function SsoSettings() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => {
    listMyOrgs()
      .then((list) => {
        setOrgs(list);
        if (list.length > 0) setSelectedOrgId(list[0].id);
      })
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedOrgId) return;
    getSsoConfig(selectedOrgId)
      .then((cfg) => setConfig(cfg || blankConfig()))
      .catch(() => setConfig(blankConfig()));
  }, [selectedOrgId]);

  const setField = (k, v) => setConfig({ ...config, [k]: v });

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateSsoConfig(selectedOrgId, config);
      setConfig(updated);
      setToast("SSO settings saved");
      setTimeout(() => setToast(""), 2400);
    } catch (err) {
      window.alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }
  if (orgs.length === 0) {
    return (
      <Panel padding={28} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>SSO Settings</div>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, fontFamily: font.body }}>
          SSO is configured per organization. Create an organization first to access SSO settings.
        </div>
        <Button variant="primary" onClick={() => navigate("/settings/organization")}>Set up organization →</Button>
      </Panel>
    );
  }

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 800, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>SSO Settings</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
              Configure SAML or OIDC for your organization. Agents sign in with their corporate IdP instead of email + password.
            </div>
          </div>
          {orgs.length > 1 && (
            <select
              value={selectedOrgId || ""}
              onChange={(e) => setSelectedOrgId(parseInt(e.target.value))}
              style={{ padding: "8px 12px", fontSize: 13, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, fontFamily: font.body, outline: "none" }}
            >
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
      </Panel>

      {config && (
        <>
          {/* Provider selection */}
          <Panel padding={20} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Provider</div>
            <div style={{ display: "flex", gap: 8 }}>
              {["saml", "oidc"].map((p) => (
                <button key={p} onClick={() => setField("provider", p)}
                  style={{
                    flex: 1, padding: "16px 20px", borderRadius: 8,
                    background: config.provider === p ? t.emerald + "10" : t.surfaceWarm,
                    border: `2px solid ${config.provider === p ? t.emerald : t.border}`,
                    cursor: "pointer", fontFamily: font.body, textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary }}>{p.toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                    {p === "saml" ? "SAML 2.0 — Okta, Azure AD, OneLogin" : "OpenID Connect — Auth0, Google, Microsoft"}
                  </div>
                </button>
              ))}
            </div>
          </Panel>

          {/* Provider-specific config */}
          {config.provider === "saml" && (
            <Panel padding={20} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>SAML Configuration</div>
              <Field label="Entity ID (Issuer)">
                <Input value={config.saml_entity_id || ""} onChange={(e) => setField("saml_entity_id", e.target.value)} placeholder="https://idp.example.com/realm" />
              </Field>
              <Field label="Single Sign-On URL">
                <Input value={config.saml_sso_url || ""} onChange={(e) => setField("saml_sso_url", e.target.value)} placeholder="https://idp.example.com/sso/saml" />
              </Field>
              <Field label="X.509 Certificate (PEM)">
                <textarea
                  value={config.saml_certificate || ""}
                  onChange={(e) => setField("saml_certificate", e.target.value)}
                  placeholder={"-----BEGIN CERTIFICATE-----\nMIIDpDC...\n-----END CERTIFICATE-----"}
                  rows={6}
                  style={{ width: "100%", padding: 12, fontSize: 11, fontFamily: font.mono, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, outline: "none", resize: "vertical" }}
                />
              </Field>
            </Panel>
          )}

          {config.provider === "oidc" && (
            <Panel padding={20} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>OIDC Configuration</div>
              <Field label="Issuer URL">
                <Input value={config.oidc_issuer || ""} onChange={(e) => setField("oidc_issuer", e.target.value)} placeholder="https://accounts.google.com" />
              </Field>
              <Field label="Client ID">
                <Input value={config.oidc_client_id || ""} onChange={(e) => setField("oidc_client_id", e.target.value)} placeholder="abc123-..." />
              </Field>
              <Field label="Client Secret">
                <Input value={config.oidc_client_secret === "(hidden)" ? "" : (config.oidc_client_secret || "")}
                  onChange={(e) => setField("oidc_client_secret", e.target.value)}
                  placeholder={config.oidc_client_secret === "(hidden)" ? "•••••• (already set — re-enter to change)" : "Client secret"}
                  type="password"
                />
              </Field>
              <Field label="Redirect URI">
                <Input value={config.oidc_redirect_uri || ""} onChange={(e) => setField("oidc_redirect_uri", e.target.value)} placeholder="https://api.realtyflows.com/sso/callback/oidc" />
              </Field>
            </Panel>
          )}

          {/* Behavior */}
          <Panel padding={20} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Behavior</div>
            <Field label="Auto-route email domain (login page redirects these users to SSO)">
              <Input value={config.email_domain || ""} onChange={(e) => setField("email_domain", e.target.value.toLowerCase())} placeholder="yourbrokerage.com" />
            </Field>
            <Field label="Default role for JIT-provisioned users">
              <Input value={config.default_role || ""} onChange={(e) => setField("default_role", e.target.value)} placeholder="agent" />
            </Field>
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={!!config.auto_provision} onChange={(e) => setField("auto_provision", e.target.checked)} />
              <span style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.body }}>
                Auto-provision users on first SSO sign-in (JIT)
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={!!config.active} onChange={(e) => setField("active", e.target.checked)} />
              <span style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.body }}>
                SSO is active
              </span>
            </label>
          </Panel>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save SSO settings"}
            </Button>
          </div>

          <div style={{ marginTop: 14, fontSize: 11, color: t.textMuted, fontFamily: font.body, padding: 14, background: t.amber + "08", borderRadius: 8 }}>
            <strong>Note:</strong> SSO handshake (SAML/OIDC callback) is stubbed on the server until <code style={{ fontFamily: font.mono }}>passport-saml</code> / <code style={{ fontFamily: font.mono }}>openid-client</code> are installed. The config below persists today and will activate as soon as those packages are deployed.
          </div>

          {toast && (
            <div style={{
              position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
              background: t.emerald, color: "#fff", padding: "10px 18px",
              borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: font.body,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
            }}>{toast}</div>
          )}
        </>
      )}
    </div>
  );
}

function blankConfig() {
  return {
    provider: "saml",
    label: "",
    saml_entity_id: "", saml_sso_url: "", saml_certificate: "",
    oidc_issuer: "", oidc_client_id: "", oidc_client_secret: "", oidc_redirect_uri: "",
    email_domain: "",
    auto_provision: false,
    default_role: "agent",
    active: true,
  };
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 5, fontFamily: font.body }}>{label}</div>
      {children}
    </div>
  );
}
